// Types mirror documentation/feature-requests/258-backend-handoff.md exactly.

export const GalleryStatus = {
  Draft: 'draft',
  Published: 'published',
} as const
export type GalleryStatus = typeof GalleryStatus[keyof typeof GalleryStatus]

export const GalleryType = {
  Media: 'media',
  External: 'external',
} as const
export type GalleryType = typeof GalleryType[keyof typeof GalleryType]

/** Processing state of the dedicated cover of an external gallery. */
export const CoverMediaStatus = {
  Pending: 'pending',
  Processing: 'processing',
  Ready: 'ready',
  Failed: 'failed',
} as const
export type CoverMediaStatus = typeof CoverMediaStatus[keyof typeof CoverMediaStatus]

export const GalleryItemType = {
  Image: 'image',
  Video: 'video',
  ExternalVideo: 'external_video',
} as const
export type GalleryItemType = typeof GalleryItemType[keyof typeof GalleryItemType]

export const GalleryItemStatus = {
  Processing: 'processing',
  Ready: 'ready',
  Failed: 'failed',
} as const
export type GalleryItemStatus = typeof GalleryItemStatus[keyof typeof GalleryItemStatus]

export const GalleryItemErrorCode = {
  ProcessingFailed: 'processing_failed',
  ImageTooManyPixels: 'image_too_many_pixels',
  UnsupportedContainer: 'unsupported_container',
  LocationMetadataPresent: 'location_metadata_present',
  MetadataUnverifiable: 'metadata_unverifiable',
} as const
export type GalleryItemErrorCode = typeof GalleryItemErrorCode[keyof typeof GalleryItemErrorCode]

export const ExternalVideoProvider = {
  YouTube: 'youtube',
  Vimeo: 'vimeo',
} as const
export type ExternalVideoProvider = typeof ExternalVideoProvider[keyof typeof ExternalVideoProvider]

export const ExternalVideoMetadataStatus = {
  Pending: 'pending',
  Fetched: 'fetched',
  Unavailable: 'unavailable',
} as const
export type ExternalVideoMetadataStatus =
  typeof ExternalVideoMetadataStatus[keyof typeof ExternalVideoMetadataStatus]

export const UploadTransport = {
  SinglePut: 'single_put',
  Multipart: 'multipart',
} as const
export type UploadTransport = typeof UploadTransport[keyof typeof UploadTransport]

/** Stable error codes returned by the gallery endpoints (handoff section 4). */
export const GalleryApiErrorCode = {
  StorageQuotaExceeded: 'storage_quota_exceeded',
  GalleryItemLimitReached: 'gallery_item_limit_reached',
  TooManyOpenUploads: 'too_many_open_uploads',
  FileTooLarge: 'file_too_large',
  UploadNotOpen: 'upload_not_open',
  SizeMismatch: 'size_mismatch',
  TooLarge: 'too_large',
  ContentInvalid: 'content_invalid',
  ObjectMissing: 'object_missing',
  MultipartIncomplete: 'multipart_incomplete',
  DuplicateExternalVideo: 'duplicate_external_video',
  ItemNotRetryable: 'item_not_retryable',
  InvalidCover: 'invalid_cover',
  ExternalGalleryHasNoMedia: 'external_gallery_has_no_media',
  GalleryNotExternal: 'gallery_not_external',
} as const
export type GalleryApiErrorCode = typeof GalleryApiErrorCode[keyof typeof GalleryApiErrorCode]

export interface ImageSources {
  avif?: string
  webp?: string
  jpeg?: string
}

export interface ImageVariant {
  name: 'thumb' | 'medium' | 'large' | 'xlarge'
  width: number
  height: number
  sources: ImageSources
}

export interface GalleryImage {
  width: number
  height: number
  variants: ImageVariant[]
}

export interface GalleryVideo {
  url: string
  mime: string
  width: number
  height: number
  duration_seconds: number | null
  poster: GalleryImage | null
}

export interface GalleryExternalVideo {
  provider: ExternalVideoProvider
  video_id: string
  title: string | null
  embed_url: string
  watch_url: string
  thumbnail: GalleryImage | null
}

export interface GalleryCounts {
  images: number
  videos: number
  external_videos: number
  hidden: number
  processing: number
  failed: number
  missing_alt_text: number
}

export interface OrganizerGalleryItem {
  id: string
  type: GalleryItemType
  position: number
  alt_text: string | null
  caption: string | null
  credit: string | null
  image: GalleryImage | null
  video: GalleryVideo | null
  external_video: GalleryExternalVideo | null
  status: GalleryItemStatus
  error_code: GalleryItemErrorCode | null
  is_hidden: boolean
  original_filename: string | null
  bytes: number
  metadata_status: ExternalVideoMetadataStatus | null
  created_at: string
  updated_at: string
}

export interface OrganizerGallery {
  id: string
  type: GalleryType
  /** https URL of an external gallery, null for a media gallery. */
  external_url: string | null
  title: string
  subtitle: string | null
  description: string | null
  position: number
  item_count: number
  cover: GalleryImage | null
  updated_at: string
  status: GalleryStatus
  published_at: string | null
  default_credit: string | null
  cover_item_id: string | null
  /** Processing state of the dedicated cover; null when there is none or for media galleries. */
  cover_media_status: CoverMediaStatus | null
  counts: GalleryCounts
  bytes_used: number
  created_at: string
  /** Present only on the show endpoint. */
  items?: OrganizerGalleryItem[]
}

export interface GalleryStorageEventUsage {
  slug: string
  name: string
  used_bytes: number
  item_count: number
}

export interface GalleryStorageLimits {
  image_max_bytes: number
  max_files_per_batch: number
  max_items_per_gallery: number
  allowed_image_mimes: string[]
  allowed_video_mimes: string[]
}

export interface GalleryStorageSummary {
  used_bytes: number
  reserved_bytes: number
  quota_bytes: number
  remaining_bytes: number
  percent_used: number
  is_over_quota: boolean
  uploads_blocked: boolean
  events: GalleryStorageEventUsage[]
  limits: GalleryStorageLimits
}

export interface GalleryPayload {
  title: string
  subtitle?: string | null
  description?: string | null
  default_credit?: string | null
  status?: GalleryStatus
  type?: GalleryType
  external_url?: string | null
}

/** The type is fixed at creation and is never sent on update. */
export interface UpdateGalleryPayload extends Omit<Partial<GalleryPayload>, 'type'> {
  cover_item_id?: string | null
}

export interface UpdateItemPayload {
  alt_text?: string | null
  caption?: string | null
  credit?: string | null
  is_hidden?: boolean
}

export interface ExternalVideoPayload {
  url: string
  alt_text?: string | null
  caption?: string | null
  credit?: string | null
}

export interface PresignedTarget {
  method: 'PUT'
  url: string
  headers: Record<string, string>
  expires_at: string
}

export interface UploadTicket {
  id: string
  kind: 'image' | 'video' | 'cover'
  transport: UploadTransport
  expires_at: string
  upload: PresignedTarget | null
  multipart: { part_size_bytes: number; part_count: number } | null
  poster_upload: PresignedTarget | null
}

export interface ReserveUploadPayload {
  mime: string
  bytes: number
  filename: string
  width?: number
  height?: number
  duration_seconds?: number
  poster?: { mime: string; bytes: number }
}

export interface ReserveCoverUploadPayload {
  mime: string
  bytes: number
  filename: string
  width?: number
  height?: number
}

export interface SignedPart {
  part_number: number
  method: 'PUT'
  url: string
  headers: Record<string, string>
}

export interface SignedParts {
  parts: SignedPart[]
  expires_at: string
}
